/**
 * Dispatch Detail Page Component
 * /admin/dispatches/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiClient from '../api/client';

const DispatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDispatch();
  }, [id]);

  const loadDispatch = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getDispatch(id);
      setDispatch(data);
    } catch (err) {
      setError(err.message || 'Failed to load dispatch');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !dispatch) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/dispatches')}>
          Back to Dispatches
        </Button>
      </Container>
    );
  }

  if (!dispatch) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/dispatches')}
            sx={{ mb: 1 }}
          >
            Back to Dispatches
          </Button>
          <Typography variant="h4" component="h1" gutterBottom>
            Dispatch Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dispatch ID: {dispatch.id.substring(0, 8)}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Booking Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Booking Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            {dispatch.booking_detail ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Booking Number:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {dispatch.booking_detail.booking_reference || dispatch.booking}
                </Typography>

                {/* A round trip is one booking with one dispatch. Dispatch.booking
                    is a OneToOne with a unique index, so the return leg cannot
                    acquire a second assignment - ADR-0003 D-04. Say so plainly,
                    or an operator will go looking for the missing return job. */}
                {dispatch.booking_detail.journey_type === 'round_trip' && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <strong>Round trip.</strong> This single assignment covers
                    both the outbound and return legs — the vehicle below serves
                    the whole journey. There is no separate return dispatch.
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        Outbound: {dispatch.booking_detail.pickup_location || '-'} →{' '}
                        {dispatch.booking_detail.dropoff_location || '-'}
                        {dispatch.booking_detail.start_datetime
                          ? ` @ ${new Date(dispatch.booking_detail.start_datetime).toLocaleString()}`
                          : ''}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Return: {dispatch.booking_detail.return_pickup_location || '-'} →{' '}
                        {dispatch.booking_detail.return_dropoff_location || '-'}
                        {dispatch.booking_detail.return_datetime
                          ? ` @ ${new Date(dispatch.booking_detail.return_datetime).toLocaleString()}`
                          : ''}
                      </Typography>
                      {dispatch.booking_detail.waiting_hours != null && (
                        <Typography variant="caption" display="block">
                          Waiting between legs: {dispatch.booking_detail.waiting_hours} h
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/admin/bookings/${dispatch.booking}`)}
                >
                  View Booking
                </Button>
              </>
            ) : (
              <Typography variant="body1">
                {dispatch.booking || '-'}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Vehicle Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Vehicle Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            {dispatch.vehicle_detail ? (
              <>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {dispatch.vehicle_detail.plate_number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dispatch.vehicle_detail.make} {dispatch.vehicle_detail.model}
                  {dispatch.vehicle_detail.year ? ` (${dispatch.vehicle_detail.year})` : ''}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => navigate(`/admin/vehicles/${dispatch.vehicle}`)}
                >
                  View Vehicle
                </Button>
              </>
            ) : (
              <Typography variant="body1">
                {dispatch.vehicle || '-'}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Dispatch Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Dispatch Details
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Dispatched At:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatDate(dispatch.dispatched_at)}
            </Typography>
            {dispatch.dispatched_by && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Dispatched By:
                </Typography>
                <Typography variant="body1">
                  {dispatch.dispatched_by || '-'}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Odometer Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Odometer Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Estimated Start Odometer:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {dispatch.estimated_start_odometer ? `${dispatch.estimated_start_odometer.toLocaleString()} km` : '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Estimated End Odometer:
            </Typography>
            <Typography variant="body1">
              {dispatch.estimated_end_odometer ? `${dispatch.estimated_end_odometer.toLocaleString()} km` : '-'}
            </Typography>
            {dispatch.estimated_start_odometer && dispatch.estimated_end_odometer && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Estimated Distance:
                </Typography>
                <Typography variant="body1">
                  {(dispatch.estimated_end_odometer - dispatch.estimated_start_odometer).toLocaleString()} km
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Instructions */}
        {dispatch.instructions && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Instructions
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body1">
                {dispatch.instructions}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Timestamps */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Timestamps
            </Typography>
            <Divider sx={{ my: 1 }} />
            {dispatch.created_at && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Created At:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {formatDate(dispatch.created_at)}
                </Typography>
              </>
            )}
            {dispatch.updated_at && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Updated At:
                </Typography>
                <Typography variant="body1">
                  {formatDate(dispatch.updated_at)}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DispatchDetail;

